// The owner portal's calls (rules 9.2–9.6). Same sign-in as the tenant portal: the portal token.
import { portalApi } from '@lipaflow/portal-core'

export interface OwnerMe {
  is_owner: boolean
  is_tenant: boolean
  name: string
  agency_name: string
  owners: { id: string; name: string; closed: boolean }[]
  waiting: number
}

export interface OwnerUnit {
  id: string
  unit: string
  building: string | null
  tenant_name: string | null
  monthly_rent: string | null
  owed: string
  overdue: string
  late: boolean
}

export interface PayoutAccount {
  method: string
  bank: string | null
  last4: string | null
  account_name: string | null
  instructions: string | null
}

export type DecisionKind = 'terms' | 'zero_deposit' | 'waiver' | 'plan' | 'repair_cost' | 'final_payout'

export interface Decision {
  kind: DecisionKind
  id: string
  owner_id: string
  owner_name: string
  title: string
  detail: string | null
  amount: string | null
  unit: string | null
  asked_at: string | null
  facts: Record<string, unknown>
}

export interface OwnerPayout {
  id: string
  period_start: string
  period_end: string
  gross_collected: string
  commission: string
  expenses: string
  net_payable: string
  status: string
  status_words: string
  reference: string | null
  final: boolean
}

export interface OwnerRepair {
  id: string
  unit: string
  building: string | null
  title: string
  status: string
  reported_on: string
  fixed_on: string | null
  cost: string | null
  cost_status: string | null
  expense_id: string | null
  /** A receipt for that cost to open. */
  has_receipt?: boolean
}

export interface StatementLine { description: string; collected_on: string; received_on?: string; gross: string; commission: string; from_earlier_months: boolean }
export interface StatementExpense { id: string; description: string; incurred_on: string; amount: string; status: string; deducted: boolean; has_receipt: boolean }
export interface OwnerStatement {
  summary: { gross_collected: string; commission: string; expenses: string; net_payable: string; from_earlier_months: string }
  /** Paid on their units, not yet split to them by the agency: not in the figures. */
  still_being_matched?: string | null
  payout: { id: string; status: string; status_words?: string; reference: string | null } | null
  collections: StatementLine[]
  expenses: StatementExpense[]
  waived: { unit: string; description: string; amount: string; owner_agreed_by: string | null }[]
}

export const ownerApi = {
  me: async () => (await portalApi.get<OwnerMe>('/owner/me')).data,
  units: async () => (await portalApi.get<OwnerUnit[]>('/owner/units')).data,
  decisions: async () => (await portalApi.get<Decision[]>('/owner/decisions')).data,
  answer: async (kind: DecisionKind, id: string, agrees: boolean, note?: string) =>
    (await portalApi.post<Decision[]>(`/owner/decisions/${kind}/${id}`, { agrees, note: note || undefined })).data,
  statement: async (year: number, month: number, ownerId?: string) =>
    (await portalApi.get<OwnerStatement>('/owner/statement', { params: { year, month, owner_id: ownerId } })).data,
  payouts: async () => (await portalApi.get<OwnerPayout[]>('/owner/payouts')).data,
  repairs: async () => (await portalApi.get<OwnerRepair[]>('/owner/repairs')).data,
  receipt: async (expenseId: string) => (await portalApi.get<{ url: string }>(`/owner/expenses/${expenseId}/receipt`)).data,
}

export function kes(value: string | number | null | undefined): string {
  return `KES ${Number(value ?? 0).toLocaleString('en-KE', { maximumFractionDigits: 2 })}`
}

export function pct(fraction: unknown): string {
  return `${Number((Number(fraction ?? 0) * 100).toFixed(2))}%`
}

export function accountWords(a: PayoutAccount | undefined): string {
  if (!a) return ''
  return [a.bank || a.method, a.last4 ? `ending ${a.last4}` : null, a.account_name].filter(Boolean).join(', ')
}

export function errorText(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

/** Where an owner was going when they had to sign in first (a decision link, rule 9.6). */
export const OWNER_NEXT_KEY = 'lipaflow.owner.next'
/** "As owner" or "As tenant", chosen once a tab (rule 9.2). */
export const PORTAL_ROLE_KEY = 'lipaflow.portal.role'

export function remember(key: string, value: string | null) {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch { /* storage may be unavailable */ }
}

export function recall(key: string): string | null {
  try { return window.sessionStorage.getItem(key) } catch { return null }
}
